import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ImageUploader } from "./ProjectEdit";

export default function LogoEdit() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/site-content").then((res) => setContent(res.data));
  }, []);

  if (!content) return <div className="p-10 text-[#5d5f64]">Loading…</div>;

  const setPath = (path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (ref[keys[i]] == null) ref[keys[i]] = {};
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const uploadLogo = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setPath("logo.image", data.path);
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api.put("/site-content", content);
      setMessage("Saved.");
      setTimeout(() => setMessage(""), 2500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-10 max-w-[860px]" data-testid="admin-logo">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-2">Brand</div>
          <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em]">
            Brand & contact
          </h1>
          <p className="text-sm text-[#5d5f64] mt-2 max-w-[60ch]">
            Identity (logo, wordmark) plus the contact details rendered in the CTA band and
            footer. All fields persist instantly to the public site.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {message && <span className="text-sm text-[#16181c]">{message}</span>}
          <button
            onClick={onSave}
            disabled={saving}
            className="bg-[#16181c] text-white px-6 py-3 font-medium hover:bg-[#5d5f64] disabled:opacity-60"
            data-testid="logo-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-[#16181c] bg-[#f3f3f0] border border-[#e3e3e1] px-4 py-3 mb-6">
          {error}
        </div>
      )}

      <Card title="Logo">
        <div className="text-xs font-medium text-[#5d5f64] mb-2">Logo image</div>
        <p className="text-xs text-[#5d5f64] mb-3">
          Upload an SVG or PNG. When present it replaces the small square mark next to the
          wordmark in the nav and footer; the wordmark text continues to appear beside it.
        </p>
        <ImageUploader
          value={content.logo?.image}
          onChange={(v) => setPath("logo.image", v)}
          onFile={uploadLogo}
          testid="logo-image"
        />
        <div className="border-t border-[#e3e3e1] pt-6 mt-6">
          <div className="text-xs font-medium text-[#5d5f64] mb-3">Wordmark (fallback)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BL label="ID" value={content.logo?.wordmark?.id} onChange={(v) => setPath("logo.wordmark.id", v)} testid="logo-wordmark-id" />
            <BL label="EN" value={content.logo?.wordmark?.en} onChange={(v) => setPath("logo.wordmark.en", v)} testid="logo-wordmark-en" />
          </div>
        </div>
      </Card>

      <Card title="Contact">
        <div className="text-xs font-medium text-[#5d5f64] mb-3">Office / address</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BL
            label="Address (ID)"
            value={content.cta?.office?.id || ""}
            onChange={(v) => setPath("cta.office.id", v)}
            testid="contact-office-id"
            multiline
          />
          <BL
            label="Address (EN)"
            value={content.cta?.office?.en || ""}
            onChange={(v) => setPath("cta.office.en", v)}
            testid="contact-office-en"
            multiline
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <BL
            label="Email"
            value={content.cta?.email || ""}
            onChange={(v) => setPath("cta.email", v)}
            testid="contact-email"
          />
          <BL
            label="Phone"
            value={content.cta?.phone || ""}
            onChange={(v) => setPath("cta.phone", v)}
            testid="contact-phone"
          />
          <BL
            label="WhatsApp (with country code, e.g. +62812…)"
            value={content.cta?.whatsapp || ""}
            onChange={(v) => setPath("cta.whatsapp", v)}
            testid="contact-whatsapp"
          />
          <BL
            label="LinkedIn URL"
            value={content.cta?.linkedin || ""}
            onChange={(v) => setPath("cta.linkedin", v)}
            testid="contact-linkedin"
          />
        </div>
      </Card>

      <div className="mt-8 flex items-center gap-4">
        <button
          onClick={onSave}
          disabled={saving}
          className="bg-[#16181c] text-white px-6 py-3 font-medium hover:bg-[#5d5f64] disabled:opacity-60"
          data-testid="logo-save-bottom"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        {message && <span className="text-sm text-[#16181c]">{message}</span>}
        <Link to="/admin" className="text-xs text-[#5d5f64] hover:text-[#16181c] ml-auto">
          ← Back to dashboard
        </Link>
      </div>

      <style>{`
        .logo-input {
          width: 100%;
          background: #ffffff;
          border: 1px solid #e3e3e1;
          padding: 10px 12px;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          color: #16181c;
        }
        .logo-input:focus { outline: none; border-color: #16181c; }
      `}</style>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-[#e3e3e1] p-6 mb-6">
      <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-5">{title}</div>
      {children}
    </section>
  );
}

function BL({ label, value, onChange, testid, multiline }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-[#5d5f64] mb-1">{label}</span>
      {multiline ? (
        <textarea
          rows={2}
          className="logo-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          className="logo-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      )}
    </label>
  );
}
